/**
 * Business Objectives List Route
 *
 * Displays searchable, filterable list of business objectives
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useListPageState } from '../../hooks/useListPageState';
import { useBusinessObjectives } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { BusinessObjective, BusinessObjectiveFilter } from '../../graphql/types';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/business-objectives/')({
  component: BusinessObjectivesListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

// Export component for testing
export { BusinessObjectivesListPage };

interface BusinessObjectiveFilters {
  search: string;
  objectiveType: string;
}

const ForgeButton = 'forge-button' as React.ElementType;

function BusinessObjectivesListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize } = Route.useSearch();

  // State for filters
  const [filters, setFilters] = useState<BusinessObjectiveFilters>({
    search: '',
    objectiveType: '',
  });

  // Build GraphQL filter
  const graphqlFilter: BusinessObjectiveFilter | undefined = filters.objectiveType
    ? { objectiveType: filters.objectiveType }
    : undefined;

  // Fetch business objectives with GraphQL
  const { data, loading, error } = useBusinessObjectives(graphqlFilter);

  // Extract business objectives from connection
  const businessObjectives = data?.businessObjectives?.edges?.map((edge) => edge.node) || [];

  const { sortColumn, sortDirection, handleSort, pagedItems, totalCount } =
    useListPageState<BusinessObjective>({
      items: businessObjectives,
      page,
      pageSize,
      filterFn: (businessObjective) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesName = businessObjective.name.toLowerCase().includes(searchLower);
          const matchesDescription = businessObjective.description
            ?.toLowerCase()
            .includes(searchLower);
          if (!matchesName && !matchesDescription) {
            return false;
          }
        }
        if (filters.objectiveType && businessObjective.objectiveType !== filters.objectiveType) {
          return false;
        }
        return true;
      },
      sortValue: (businessObjective, column) =>
        String(businessObjective[column as keyof BusinessObjective] ?? ''),
    });

  // DataTable columns
  const columns: DataTableColumn<BusinessObjective>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '35%',
      render: (businessObjective) => (
        <div>
          <div className="entity-list-name-cell">{businessObjective.name}</div>
          {businessObjective.description && (
            <div className="entity-list-description-cell">
              {businessObjective.description.substring(0, 60)}
              {businessObjective.description.length > 60 ? '...' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'objectiveType',
      label: 'Objective Type',
      sortable: true,
      width: '20%',
      render: (businessObjective) => <span>{businessObjective.objectiveType || '—'}</span>,
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      width: '20%',
      render: (businessObjective) => <span>{businessObjective.priority || '—'}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: '25%',
      render: (businessObjective) => <span>{businessObjective.status || '—'}</span>,
    },
  ];

  const handleRowClick = (businessObjective: BusinessObjective): void => {
    navigate({ to: `/business-objectives/${businessObjective.entityId}` });
  };

  const handleFilterChange = (newFilters: Partial<BusinessObjectiveFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    void navigate({ to: '/business-objectives', search: { page: 1, pageSize } });
  };

  const handleClearFilters = (): void => {
    setFilters({ search: '', objectiveType: '' });
    void navigate({ to: '/business-objectives', search: { page: 1, pageSize } });
  };

  const handlePageChange = (newPage: number): void => {
    void navigate({ to: '/business-objectives', search: { page: newPage, pageSize } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({
      to: '/business-objectives',
      search: { page: 1, pageSize: newPageSize },
    });
  };

  if (error) {
    return (
      <div className="business-objectives-list-page">
        <h1 className="forge-typography--headline4">Business Objectives</h1>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load business objectives"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="business-objectives-list-page">
      <div className="business-objectives-header">
        <div>
          <h1 className="forge-typography--headline4">Business Objectives</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${totalCount} business objectives`}
          </p>
        </div>
        <PermissionGate permission="business-objective:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => navigate({ to: '/business-objectives/new' })}
          >
            Create Business Objective
          </ForgeButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search business objectives..."
            aria-label="Search business objectives"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>

        <select
          className="filter-select"
          aria-label="Filter by objective type"
          value={filters.objectiveType}
          onChange={(e) => handleFilterChange({ objectiveType: e.target.value })}
        >
          <option value="">All Objective Types</option>
          <option value="strategic">Strategic</option>
          <option value="operational">Operational</option>
          <option value="financial">Financial</option>
          <option value="customer">Customer</option>
          <option value="innovation">Innovation</option>
          <option value="compliance">Compliance</option>
        </select>

        {(filters.search || filters.objectiveType) && (
          <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </ForgeButton>
        )}
      </div>

      {/* Data Table */}
      <div className="business-objectives-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={4} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedItems}
              getRowKey={(businessObjective) => businessObjective.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              emptyState={{
                icon: 'inbox',
                title: 'No business objectives found',
                description:
                  filters.search || filters.objectiveType
                    ? 'Try adjusting your filters'
                    : 'Create your first business objective to get started',
                actionLabel: 'Create Business Objective',
                onAction: () => navigate({ to: '/business-objectives/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
